import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { Users, TrendingUp, UserPlus, UserMinus, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './UserAnalytics.css';

const UserAnalytics = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const isDarkTheme = document.documentElement.getAttribute('data-theme') === 'dark';
  const chartGridStroke = isDarkTheme ? '#3d2a5c' : '#f1f5f9';
  const chartAxisStroke = isDarkTheme ? '#a78bfa' : '#6b7280';
  const chartTooltipStyle = {
    backgroundColor: isDarkTheme ? '#1f0d2e' : 'white',
    border: isDarkTheme ? '1px solid rgba(100, 32, 172, 1)' : '1px solid #e5e7eb',
    borderRadius: '8px',
    color: isDarkTheme ? '#e9d5ff' : '#374151',
  };

  // Mock data for charts
  const userGrowthData = [
    { month: 'Jan', users: 45, newUsers: 5, active: 42 },
    { month: 'Feb', users: 52, newUsers: 7, active: 48 },
    { month: 'Mar', users: 49, newUsers: 3, active: 45 },
    { month: 'Apr', users: 58, newUsers: 9, active: 54 },
    { month: 'May', users: 61, newUsers: 3, active: 57 },
    { month: 'Jun', users: 67, newUsers: 6, active: 63 },
    { month: 'Jul', users: 64, newUsers: 2, active: 59 },
    { month: 'Aug', users: 71, newUsers: 7, active: 67 }
  ];

  const statusDistribution = [
    { name: 'Online', value: 4, color: '#16a34a' },
    { name: 'Offline', value: 2, color: '#6b7280' },
    { name: 'Not Assigned', value: 1, color: '#f59e0b' }
  ];

  const adherenceByUser = [
    { id: 42, name: 'Patient 42', adherence: 85, lastActive: '2 hours ago', risk: 'low' },
    { id: 43, name: 'Patient 43', adherence: 45, lastActive: '1 day ago', risk: 'medium' },
    { id: 44, name: 'Patient 44', adherence: 12, lastActive: '3 days ago', risk: 'high' },
    { id: 45, name: 'Patient 45', adherence: 92, lastActive: '30 minutes ago', risk: 'low' },
    { id: 46, name: 'Patient 46', adherence: 67, lastActive: '4 hours ago', risk: 'low' },
    { id: 47, name: 'Patient 47', adherence: 78, lastActive: '1 hour ago', risk: 'low' },
    { id: 48, name: 'Patient 48', adherence: 23, lastActive: '2 days ago', risk: 'high' }
  ];

  const handleViewPatient = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#16a34a';
      default: return '#6b7280';
    }
  };

  return (
    <div className="user-analytics">
      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: '#eff6ff', color: '#3b82f6' }}>
            <Users size={24} />
          </div>
          <div className="card-content">
            <h3>Total Users</h3>
            <div className="card-value">22</div>
            <div className="card-change positive">+7 this month</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: '#f0fdf4', color: '#16a34a' }}>
            <UserPlus size={24} />
          </div>
          <div className="card-content">
            <h3>New Users</h3>
            <div className="card-value">7</div>
            <div className="card-change positive">+2 from last month</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
            <UserMinus size={24} />
          </div>
          <div className="card-content">
            <h3>Inactive Users</h3>
            <div className="card-value">4</div>
            <div className="card-change negative">+1 from last week</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon" style={{ backgroundColor: '#f0f9ff', color: '#0891b2' }}>
            <TrendingUp size={24} />
          </div>
          <div className="card-content">
            <h3>Avg. Adherence</h3>
            <div className="card-value">58.9%</div>
            <div className="card-change positive">+3.2% this week</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="analytics-charts">
        <div className="chart-container">
          <div className="chart-header">
            <h3>User Growth Over Time</h3>
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="time-selector"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                <XAxis dataKey="month" stroke={chartAxisStroke} />
                <YAxis stroke={chartAxisStroke} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="active" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h3>Device Status Distribution</h3>
          </div>
          <div className="chart-content">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* User List Table */}
      <div className="user-list-section">
        <div className="section-header">
          <h3>Individual User Performance</h3>
          <p>Click on any user to view detailed dashboard</p>
        </div>
        
        <div className="user-table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>Patient ID</th>
                <th>Name</th>
                <th>Adherence Rate</th>
                <th>Last Active</th>
                <th>Risk Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {adherenceByUser.map((user) => (
                <tr key={user.id} className="user-row">
                  <td className="patient-id">#{user.id}</td>
                  <td className="patient-name">{user.name}</td>
                  <td>
                    <div className="adherence-cell">
                      <div className="adherence-bar">
                        <div 
                          className="adherence-fill"
                          style={{ 
                            width: `${user.adherence}%`,
                            backgroundColor: user.adherence >= 75 ? '#16a34a' : user.adherence >= 50 ? '#f59e0b' : '#dc2626'
                          }}
                        ></div>
                      </div>
                      <span className="adherence-value">{user.adherence}%</span>
                    </div>
                  </td>
                  <td className="last-active">{user.lastActive}</td>
                  <td>
                    <span 
                      className="risk-badge"
                      style={{ 
                        backgroundColor: getRiskColor(user.risk),
                        color: 'white'
                      }}
                    >
                      {user.risk}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="view-button"
                      onClick={() => handleViewPatient(user.id)}
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserAnalytics;



